import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";

const categories = ["Health", "Grief", "Guidance", "Family", "Work", "Relationship", "Peace", "Gratitude", "Other"];

const initial = {
  submitter_name: "", email: "", title: "", prayer_text: "", location: "",
  category: "Guidance", for_whom: "self", privacy_level: "first_name",
  consent_monday: false, consent_public: false, anonymous: false,
  display_name: "", practical_help: "", guidelines_ack: false,
};

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mono-label block mb-2">{label}</span>
      {hint && <span className="font-body text-sm text-ink-tertiary block mb-2 italic">{hint}</span>}
      {children}
    </label>
  );
}

const inputCls = "w-full bg-transparent border border-ink/25 px-4 py-3 font-body text-lg text-ink focus:border-ink outline-none";

export default function SubmitPrayer() {
  const [f, setF] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.guidelines_ack) { setError("Please acknowledge the community guidelines and privacy policy."); return; }
    setBusy(true);
    try {
      await api.post("/prayers", f);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center animate-fade-in" data-testid="prayer-received">
        <span className="stamp mb-8 inline-block">Prayer received</span>
        <h1 className="font-heading text-4xl md:text-5xl text-ink mt-6">Prayer received.</h1>
        <p className="font-body text-lg text-ink-secondary mt-6 leading-relaxed">
          Thank you for entrusting this to the circle. Your prayer will be reviewed with care before being shared.
        </p>
        <div className="flex gap-4 justify-center mt-12">
          <button onClick={() => { setF(initial); setSubmitted(false); }} className="btn-ink" data-testid="submit-another">Submit another</button>
          <button onClick={() => navigate("/vigil")} className="btn-solid">Enter the Vigil</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-10 py-20">
      <p className="mono-label mb-4">Submit a Prayer</p>
      <h1 className="font-heading text-4xl md:text-5xl text-ink leading-none">Entrust something to the circle</h1>
      <p className="font-body text-lg text-ink-secondary mt-6 leading-relaxed">
        Please do not include information that identifies another person without their permission. Nothing is published automatically — every prayer is read with care first.
      </p>

      <form onSubmit={submit} className="mt-12 space-y-8" data-testid="submit-form">
        <Field label="Your name"><input className={inputCls} required value={f.submitter_name} onChange={(e) => set("submitter_name", e.target.value)} data-testid="field-name" /></Field>
        <Field label="Your email" hint="Never shown publicly. Used for updates about your prayer.">
          <input type="email" className={inputCls} required value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="field-email" />
        </Field>
        <Field label="Prayer title or person's first name">
          <input className={inputCls} required value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="field-title" />
        </Field>
        <Field label="Prayer request">
          <textarea rows={6} className={inputCls} required value={f.prayer_text} onChange={(e) => set("prayer_text", e.target.value)} data-testid="field-prayer" />
        </Field>

        <div className="grid sm:grid-cols-2 gap-8">
          <Field label="Category">
            <select className={inputCls} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="field-category">
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Location" hint="City / region / country only (optional)">
            <input className={inputCls} value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="field-location" />
          </Field>
        </div>

        <Field label="Is this prayer for you or someone else?">
          <select className={inputCls} value={f.for_whom} onChange={(e) => set("for_whom", e.target.value)} data-testid="field-forwhom">
            <option value="self">For me</option>
            <option value="other">For someone else</option>
          </select>
        </Field>

        <Field label="Privacy preference">
          <select className={inputCls} value={f.privacy_level} onChange={(e) => set("privacy_level", e.target.value)} data-testid="field-privacy">
            <option value="public">Public</option>
            <option value="first_name">First name only</option>
            <option value="anonymous">Anonymous</option>
            <option value="private">Private — administrators only</option>
          </select>
        </Field>

        <Field label="How would you like this person referred to publicly? (optional)">
          <input className={inputCls} value={f.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="field-displayname" />
        </Field>

        <Field label="Is there a practical way someone might help? (optional)" hint="e.g. an introduction, transport, a letter, a meal. Only shared if you and an administrator agree.">
          <textarea rows={3} className={inputCls} value={f.practical_help} onChange={(e) => set("practical_help", e.target.value)} data-testid="field-help" />
        </Field>

        <div className="space-y-4 paper-rule pt-8">
          {[
            ["consent_monday", "You may include this prayer in the Monday prayer email."],
            ["consent_public", "You may show this prayer publicly in The Vigil."],
            ["anonymous", "I would like to remain anonymous."],
          ].map(([k, label]) => (
            <label key={k} className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} className="mt-1.5 w-4 h-4 accent-ink" data-testid={`check-${k}`} />
              <span className="font-body text-ink-secondary leading-relaxed">{label}</span>
            </label>
          ))}
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={f.guidelines_ack} onChange={(e) => set("guidelines_ack", e.target.checked)} className="mt-1.5 w-4 h-4 accent-ink" data-testid="check-guidelines" />
            <span className="font-body text-ink-secondary leading-relaxed">I acknowledge the community guidelines and privacy policy, and understand the Ministry is not an emergency or crisis service.</span>
          </label>
        </div>

        {error && <p className="font-body text-terracotta" data-testid="submit-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-solid w-full" data-testid="submit-button">
          {busy ? "Leaving this here…" : "Leave this with the circle"}
        </button>
      </form>
    </div>
  );
}
